import { useId, useRef, useState } from 'react';
import type { JSX } from 'react';
import { Button, IconChevronDown, IconChevronRight, IconProjects, Input, OptionList, SettingsInfo, SettingsMenu } from '@taki/ui';
import type { SettingsMenuOption } from '@taki/ui';
import type { ChampAChoix } from '../catalogues.js';
import { PAS_MINUTES, clock } from './journal.js';
import { decalerMinutes } from './decaler-horaire.js';

export function parseClock(value: string): number | null {
  const match = /^(\d{1,2})(?:[:h](\d{2})?)?$/.exec(value.trim().toLowerCase());
  if (match == null) return null;
  const hours = Number(match[1] ?? '');
  const minutes = Number(match[2] ?? 0);
  if (hours > 24 || minutes > 59 || (hours === 24 && minutes !== 0)) return null;
  return hours * 60 + minutes;
}

export function dureeLisible(minutes: number | null): string {
  if (minutes == null || minutes < PAS_MINUTES) return 'À définir';
  const heures = Math.floor(minutes / 60);
  const reste = minutes % 60;
  return [heures > 0 ? `${heures} h` : '', reste > 0 ? `${reste} min` : ''].filter(Boolean).join(' ');
}

const labelStyle = 'flex min-w-0 flex-col gap-1.5 text-xs font-medium text-ink-500';
const inputStyle = 'h-9 rounded-sm bg-surface px-2.5 text-sm focus-visible:shadow-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-accent-border';
/* LES RACCOURCIS ET LES FLECHES DES HEURES sont hors du parcours de Tab
   (`tabIndex={-1}`) : Tab va de champ en champ (Tâche, Jour, Début, Fin,
   Notes) puis au bouton qui valide. Ils restent a la souris, et les fleches
   du clavier ajustent l'heure dans son champ. */
const raccourciStyle ='h-8 w-full min-w-0 rounded-none border-0 px-1.5 text-[0.6875rem] font-medium tabular-nums aria-disabled:text-ink-300 focus-visible:shadow-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-accent-border';

function ChoixTache({ options, value, onChange, readOnly }: {
  options: readonly SettingsMenuOption[];
  value: string;
  onChange: (value: string) => void;
  readOnly: boolean;
}): JSX.Element {
  const id = useId();
  const [ouvert, setOuvert] = useState(false);
  const bouton = useRef<HTMLButtonElement>(null);
  const choisie = options.find((option) => option.value === value);
  return (
    <div className={labelStyle}>
      <div className="flex h-6 min-w-0 items-center justify-between gap-3">
        <span id={`${id}-label`} className="shrink-0">Tâche</span>
        {choisie !== undefined && <span id={`${id}-projet`} className="inline-flex min-w-0 max-w-[80%] items-center gap-1.5 rounded-sm border border-border-soft bg-surface-1 px-2 py-1 text-[0.6875rem] font-normal leading-none text-ink-500" title={choisie.prefixe}>
          <IconProjects size={12} className="shrink-0" aria-hidden="true" />
          <span className="sr-only">Projet : </span>
          <span className="truncate">{choisie.prefixe}</span>
        </span>}
      </div>
      <div className="relative min-w-0">
        <Button
          ref={bouton}
          variant="secondary"
          className="h-9 w-full justify-start gap-2 px-2.5 text-sm font-normal"
          aria-labelledby={`${id}-label ${id}-valeur`}
          aria-describedby={choisie === undefined ? undefined : `${id}-projet`}
          aria-haspopup="listbox"
          aria-expanded={ouvert}
          disabled={readOnly}
          title={choisie === undefined ? 'Choisir une tâche' : `${choisie.prefixe} › ${choisie.label}`}
          onClick={() => setOuvert((actuel) => !actuel)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') { event.preventDefault(); setOuvert(true); }
            /* SANS TACHE CHOISIE, Entree ouvre la liste : valider rendrait
               seulement « Choisir une tâche ». Avec une tache, Entree remonte
               a la fenetre, qui valide (`clavier-creneau.ts`). */
            if (event.key === 'Enter' && choisie === undefined) { event.preventDefault(); setOuvert(true); }
          }}
        >
          <span id={`${id}-valeur`} className={`min-w-0 flex-1 truncate text-left ${choisie === undefined ? 'text-ink-400' : 'text-ink-900'}`}>
            {choisie?.label ?? 'Choisir une tâche…'}
          </span>
          <IconChevronDown size={12} className="shrink-0 text-ink-400" />
        </Button>
        {ouvert && <OptionList
          compact
          recherche
          options={options}
          value={choisie?.value ?? null}
          vide="Aucune tâche trouvée"
          onPick={(valeur) => {
            if (valeur == null) return;
            onChange(valeur);
            setOuvert(false);
            bouton.current?.focus();
          }}
          onClose={() => setOuvert(false)}
        />}
      </div>
      {options.length === 0 && <span className="text-xs font-normal">Aucune tâche disponible</span>}
    </div>
  );
}

function Heure({ label, value, onChange, readOnly, minimum = 0, fin = false, onNow }: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  readOnly: boolean;
  minimum?: number;
  fin?: boolean;
  onNow?: () => void;
}): JSX.Element {
  const id = useId();
  const minutes = parseClock(value);
  const maximum = fin ? 1440 : 1440 - PAS_MINUTES;
  const invalide = minutes == null || minutes % PAS_MINUTES !== 0 || minutes < minimum || minutes > maximum;
  const erreur = minutes != null && minutes < minimum ? 'La fin doit suivre le début' : 'Heure valide par pas de 5 min';
  const disponible = (delta: number): boolean => !readOnly && minutes != null && minutes % PAS_MINUTES === 0 && decalerMinutes(minutes, delta, maximum) != null;
  const ajuster = (delta: number): void => { if (disponible(delta) && minutes != null) onChange(clock(minutes + delta)); };
  return (
    <div className={labelStyle}>
      <div className="flex h-4 min-w-0 items-center gap-2">
        <label htmlFor={id} className="shrink-0">{label}</label>
        {invalide && <span id={`${id}-erreur`} role="alert" title={erreur} className="min-w-0 truncate text-[0.6875rem] font-normal text-danger">{erreur}</span>}
      </div>
      <div className="overflow-hidden rounded-sm border border-border bg-surface focus-within:border-accent-border">
        <div className="flex h-10 w-full min-w-0 items-stretch">
          <Input
            id={id}
            value={value}
            readOnly={readOnly}
            aria-invalid={invalide}
            aria-describedby={invalide ? `${id}-erreur` : undefined}
            className="h-full min-w-0 flex-1 rounded-none border-0 bg-transparent px-2.5 text-base font-medium tabular-nums focus-visible:shadow-none"
            onChange={(event) => onChange(event.target.value)}
            onBlur={() => { if (!invalide && minutes != null) onChange(clock(minutes)); }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                event.preventDefault(); ajuster(event.key === 'ArrowUp' ? PAS_MINUTES : -PAS_MINUTES);
              }
            }}
          />
          {!readOnly && <div className="flex w-7 shrink-0 flex-col border-l border-border-soft">
            {[5, -5].map((delta) => <Button
              key={delta}
              variant="ghost"
              size="sm"
              tabIndex={-1}
              className="h-5 min-h-0 w-7 rounded-none p-0 aria-disabled:text-ink-300 focus-visible:shadow-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-accent-border"
              aria-label={`${label} : ${delta > 0 ? 'ajouter' : 'retirer'} 5 minutes`}
              aria-disabled={!disponible(delta)}
              title={`${delta > 0 ? '+' : '−'}5 min`}
              onClick={() => ajuster(delta)}
            ><IconChevronRight size={11} className={delta > 0 ? '-rotate-90' : 'rotate-90'} /></Button>)}
          </div>}
        </div>
        {!readOnly && <div className="grid grid-cols-3 divide-x divide-border-soft border-t border-border-soft bg-surface-1">
          <Button variant="ghost" size="sm" tabIndex={-1} className={raccourciStyle} aria-label={`${label} : retirer 15 minutes`} aria-disabled={!disponible(-15)} title="Retirer 15 minutes" onClick={() => ajuster(-15)}>−15 min</Button>
          {onNow !== undefined && <Button variant="ghost" size="sm" tabIndex={-1} className={raccourciStyle} aria-label={`${label} : maintenant`} title={fin ? "Heure actuelle, arrondie aux 5 minutes" : "Heure actuelle, arrondie aux 5 minutes ; conserve la durée"} onClick={onNow}>Maintenant</Button>}
          <Button variant="ghost" size="sm" tabIndex={-1} className={raccourciStyle} aria-label={`${label} : ajouter 15 minutes`} aria-disabled={!disponible(15)} title="Ajouter 15 minutes" onClick={() => ajuster(15)}>+15 min</Button>
        </div>}
      </div>
    </div>
  );
}

export interface FormulaireCreneauProps {
  options: readonly SettingsMenuOption[];
  tache: string;
  jour: string;
  debut: string;
  fin: string;
  notes: string;
  choix: Record<string, string>;
  choixDuJeu: readonly ChampAChoix[];
  readOnly: boolean;
  onTache: (value: string) => void;
  onJour: (value: string) => void;
  onDebut: (value: string) => void;
  onFin: (value: string) => void;
  onNotes: (value: string) => void;
  onChoix: (key: string, value: string) => void;
  onNow: () => void;
  onNowFin: () => void;
}

export function FormulaireCreneau(props: FormulaireCreneauProps): JSX.Element {
  const { options, tache, jour, debut, fin, notes, choix, choixDuJeu, readOnly, onTache, onJour, onDebut, onFin, onNotes, onChoix, onNow, onNowFin } = props;
  const debutMinutes = parseClock(debut);
  const finMinutes = parseClock(fin);
  const duree = debutMinutes == null || finMinutes == null ? null : finMinutes - debutMinutes;
  const minimum = debutMinutes == null ? 0 : Math.min(1440, debutMinutes + PAS_MINUTES);
  const date = <label className={labelStyle}>Jour<Input type="date" className={`${inputStyle} min-w-0`} value={jour} readOnly={readOnly} onChange={(event) => onJour(event.target.value)} /></label>;
  const depart = <Heure label="Début" value={debut} onChange={onDebut} readOnly={readOnly} onNow={onNow} />;
  const arrivee = <Heure label="Fin" value={fin} onChange={onFin} readOnly={readOnly} fin minimum={minimum} onNow={onNowFin} />;
  return (
    <div className="flex flex-col gap-5">
      <ChoixTache options={options} value={tache} onChange={onTache} readOnly={readOnly} />
      {choixDuJeu.map((champ) => <div key={champ.key} className={labelStyle}>
        <span>{champ.label}</span>
        <SettingsMenu label={champ.label} value={choix[champ.key] ?? ''} options={[{ value: '', label: `Sans ${champ.label.toLocaleLowerCase('fr-FR')}` }, ...champ.options]} disabled={readOnly} onChange={(value) => onChoix(champ.key, value)} />
      </div>)}
      {date}
      <div>
        <div className="grid grid-cols-1 gap-3 min-[420px]:grid-cols-2">{depart}{arrivee}</div>
        <SettingsInfo>
          Durée du créneau : <strong className="font-semibold tabular-nums text-ink-900" role="status">{dureeLisible(duree)}</strong>
        </SettingsInfo>
      </div>
      <label className={labelStyle}>Notes <span className="sr-only">(facultatif)</span><textarea rows={2} className="min-h-16 w-full resize-y rounded-sm border border-border bg-surface px-3 py-2 text-sm font-normal text-ink-900 placeholder:text-ink-300 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-accent-border" value={notes} readOnly={readOnly} onChange={(event) => onNotes(event.target.value)} placeholder="Complément d’infos…" /></label>
    </div>
  );
}
